import {
  Users,
  Building2,
  DollarSign,
  TrendingUp,
  CheckSquare,
  AlertTriangle,
  Phone,
  Mail,
  CalendarDays,
  StickyNote,
  Clock,
  Trophy,
  ShieldAlert,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useDashboard } from '@/lib/hooks/crm/useDashboard';

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('en-US').format(value);
}

const activityTypeIcons: Record<string, React.ElementType> = {
  call: Phone,
  email: Mail,
  meeting: CalendarDays,
  note: StickyNote,
};

const priorityColors: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
};

export function DashboardView() {
  const { metrics, loading } = useDashboard();

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!metrics) {
    return <div className="text-center py-16 text-muted-foreground">Failed to load dashboard</div>;
  }

  const { counts, revenue, pipelineBreakdown, recentActivities, upcomingTasks, dealRiskSummary } =
    metrics;

  // Calculate pipeline bar widths
  const maxStageValue = Math.max(...pipelineBreakdown.map((s) => s.totalValue), 1);

  return (
    <div className="p-6 space-y-6 overflow-auto">
      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          title="Contacts"
          value={formatNumber(counts.contacts)}
          icon={Users}
          color="text-blue-600"
        />
        <MetricCard
          title="Companies"
          value={formatNumber(counts.companies)}
          icon={Building2}
          color="text-purple-600"
        />
        <MetricCard
          title="Pipeline Value"
          value={formatCurrency(revenue.totalPipelineValue)}
          icon={DollarSign}
          color="text-green-600"
        />
        <MetricCard
          title="Avg Deal Value"
          value={formatCurrency(revenue.avgDealValue)}
          icon={TrendingUp}
          color="text-emerald-600"
        />
        <MetricCard
          title="Open Deals"
          value={formatNumber(counts.openDeals)}
          icon={DollarSign}
          color="text-indigo-600"
        />
        <MetricCard
          title="Won Deals"
          value={formatNumber(counts.wonDeals)}
          subtitle={formatCurrency(revenue.wonValue)}
          icon={Trophy}
          color="text-green-600"
        />
        <MetricCard
          title="Active Tasks"
          value={formatNumber(counts.tasks)}
          icon={CheckSquare}
          color="text-orange-600"
        />
        <MetricCard
          title="Overdue Tasks"
          value={formatNumber(counts.overdueTasks)}
          icon={AlertTriangle}
          color={counts.overdueTasks > 0 ? 'text-red-600' : 'text-muted-foreground'}
          alert={counts.overdueTasks > 0}
        />
      </div>

      {/* Pipeline Breakdown + Activity/Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Stage Breakdown */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Pipeline Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {pipelineBreakdown.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No pipeline data</p>
            ) : (
              <div className="space-y-3">
                {pipelineBreakdown.map((stage) => (
                  <div key={stage.stageId} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: stage.stageColor }}
                        />
                        <span className="font-medium">{stage.stageName}</span>
                        <span className="text-muted-foreground">({stage.dealCount})</span>
                      </div>
                      <span className="text-muted-foreground">
                        {formatCurrency(stage.totalValue)}
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          backgroundColor: stage.stageColor,
                          width: `${(stage.totalValue / maxStageValue) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivities.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No recent activity</p>
            ) : (
              <div className="space-y-3">
                {recentActivities.slice(0, 8).map((activity) => {
                  const Icon = activityTypeIcons[activity.activityType] || Clock;
                  return (
                    <div key={activity.id} className="flex items-start gap-3">
                      <div className="mt-0.5">
                        <Icon className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{activity.subject}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{new Date(activity.occurredAt).toLocaleDateString()}</span>
                          {activity.contactName && <span>• {activity.contactName}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Deal Risk Alerts */}
      {(dealRiskSummary.high > 0 || dealRiskSummary.medium > 0) && (
        <Card className="border-amber-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-amber-500" />
              Deal Risk Alerts
              <div className="flex gap-1.5 ml-auto">
                {dealRiskSummary.high > 0 && (
                  <Badge variant="secondary" className="bg-red-100 text-red-700 text-xs">
                    {dealRiskSummary.high} high
                  </Badge>
                )}
                {dealRiskSummary.medium > 0 && (
                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 text-xs">
                    {dealRiskSummary.medium} medium
                  </Badge>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dealRiskSummary.topRisks.map((deal) => (
                <div
                  key={deal.id}
                  className={`flex items-center gap-3 p-2 rounded border ${
                    deal.riskLevel === 'high'
                      ? 'border-red-200 bg-red-50/50'
                      : 'border-yellow-200 bg-yellow-50/50'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{deal.name}</p>
                      <span className="text-sm font-semibold text-muted-foreground">
                        {formatCurrency(deal.value)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {deal.stageName}
                      </Badge>
                      {deal.riskFlags.map((flag) => (
                        <Badge
                          key={flag}
                          variant="secondary"
                          className={`text-xs ${
                            flag === 'overdue_close' || flag === 'stale_deal'
                              ? 'bg-red-100 text-red-600'
                              : 'bg-amber-100 text-amber-600'
                          }`}
                        >
                          {flag.replace(/_/g, ' ')}
                        </Badge>
                      ))}
                      {deal.daysInStage > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {deal.daysInStage}d in stage
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Tasks */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Upcoming Tasks</CardTitle>
        </CardHeader>
        <CardContent>
          {upcomingTasks.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No upcoming tasks</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {upcomingTasks.map((task) => {
                const overdue = task.dueDate && new Date(task.dueDate) < new Date();
                return (
                  <div
                    key={task.id}
                    className={`flex items-center gap-3 border rounded-lg p-3 ${
                      overdue ? 'border-red-300 bg-red-50/50' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{task.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge
                          variant="secondary"
                          className={`text-xs ${priorityColors[task.priority] ?? ''}`}
                        >
                          {task.priority}
                        </Badge>
                        {task.dueDate && (
                          <span
                            className={`text-xs ${overdue ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}
                          >
                            {overdue ? 'Overdue: ' : ''}
                            {new Date(task.dueDate).toLocaleDateString()}
                          </span>
                        )}
                        {task.contactName && (
                          <span className="text-xs text-muted-foreground truncate">
                            {task.contactName}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  color,
  alert,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ElementType;
  color: string;
  alert?: boolean;
}) {
  return (
    <Card className={alert ? 'border-red-300' : ''}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground font-medium">{title}</p>
            <p className="text-xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <Icon className={`h-8 w-8 ${color} opacity-60`} />
        </div>
      </CardContent>
    </Card>
  );
}
